/**
 * "Print (thermal)" for any document — invoice, gold purchase, repair,
 * issue/receipt voucher, chit installment receipt. Fetches the monospace
 * preview from `<printPath>/print/preview`, shows it in a modal (project-
 * plan §11: "a true-width monospace preview... before you commit paper"),
 * then on confirm fetches `<printPath>/print` as raw ESC/POS bytes and hands
 * them to useBluetoothPrinter.
 *
 * Renders nothing when Web Bluetooth isn't available (iOS) — the existing
 * A4/PDF print button next to this one still works there.
 */
import { useState } from 'react';
import { Bluetooth, Printer } from 'lucide-react';
import { api, ApiClientError } from '../lib/api.js';
import { useBluetoothPrinter } from '../hooks/useBluetoothPrinter.js';
import { Button } from './ui/Button.jsx';
import { Modal } from './ui/Modal.jsx';

export function ThermalPrintButton({ printPath, compact = false, disabled = false }) {
  const bt = useBluetoothPrinter();
  const [open, setOpen] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [previewBusy, setPreviewBusy] = useState(false);
  const [printError, setPrintError] = useState(null);

  const width = bt.defaultPrinter?.paper_width === '80' ? '80' : '58';

  const openPreview = async () => {
    setPrintError(null);
    setPreviewBusy(true);
    try {
      const { text } = await api.get(`${printPath}/print/preview`, { query: { width } });
      setPreviewText(text);
      setOpen(true);
    } catch (err) {
      setPrintError(err instanceof ApiClientError ? err.message : 'Could not build the receipt preview.');
    } finally {
      setPreviewBusy(false);
    }
  };

  const confirmPrint = async () => {
    setPrintError(null);
    try {
      const res = await api.raw(`${printPath}/print`, { query: { width } });
      const bytes = await res.arrayBuffer();
      await bt.print(bytes);
      setOpen(false);
    } catch (err) {
      setPrintError(err instanceof ApiClientError ? err.message : err.message || 'Could not print.');
    }
  };

  if (!bt.isSupported) return null;

  const busy = bt.status === 'connecting' || bt.status === 'printing';

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={openPreview}
          disabled={disabled || previewBusy}
          className="text-muted hover:text-primary disabled:opacity-50"
          aria-label="Print (thermal)"
        >
          <Bluetooth size={16} aria-hidden="true" />
        </button>
      ) : (
        <Button variant="secondary" onClick={openPreview} loading={previewBusy} disabled={disabled} className="flex-1 sm:flex-none">
          <Printer size={17} aria-hidden="true" /> Print (thermal)
        </Button>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Thermal receipt preview">
        <div className="flex flex-col gap-4">
          {!bt.defaultPrinter ? (
            <p className="rounded-card border border-warning/30 bg-warning/10 px-3.5 py-3 text-sm text-warning">
              No printer saved yet. Pair one in Settings, then come back here.
            </p>
          ) : null}
          <pre className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap rounded-card border border-border bg-surface-2 p-3 font-mono text-xs leading-relaxed">
            {previewText}
          </pre>
          {printError ? <p className="text-sm text-danger">{printError}</p> : null}
          {busy ? (
            <p className="text-sm text-muted">{bt.status === 'connecting' ? 'Connecting to printer…' : 'Printing…'}</p>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={confirmPrint} loading={busy} disabled={!bt.defaultPrinter}>
              Print
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default ThermalPrintButton;
